import { Controller } from "@hotwired/stimulus"
import { parseNumber, formatNumber } from "../lib/number_helper"

export default class extends Controller {
  static targets = [
    "amountInput",
    "totalAmount",
    "row",
    "connectedPriceInput",
    "weightedPricePerMwh",
    "headerAssignedAmount",
    "emptyResultMessage",
    "technologyInput",
    "countryInput",
    "qualityInput",
    "quarterInput",
    "quarterTitle",
  ]

  static values = {
    technologyGroups: Object,
    qualityGroups: Object,
    countryGroups: Object,
    quarterTranslations: Object,
  }

  connect() {
    this.updateDeliveryConnectionTable()
    this.updateQuarterTitle()
  }

  updateQuarterTitle() {
    if (this.hasQuarterTitleTarget && this.hasQuarterInputTarget) {
      const quarter = this.quarterInputTarget.value
      this.quarterTitleTarget.textContent =
        this.quarterTranslationsValue[quarter]
    }
  }

  updateHeaders(event) {
    const newDeliveryAmount = parseNumber(event.target.value)

    this.headerAssignedAmountTargets.forEach((headerAssignedAmountTarget) => {
      const hknItemShare = parseNumber(
        headerAssignedAmountTarget.dataset.hknItemShare,
      )
      const subsidyShare = parseNumber(
        headerAssignedAmountTarget.dataset.subsidyShare || "0",
      )
      const newAmountForHeader =
        newDeliveryAmount * hknItemShare - newDeliveryAmount * subsidyShare

      headerAssignedAmountTarget.textContent = newAmountForHeader.toFixed(3)
    })
  }

  updateAmount() {
    if (this.hasTotalAmountTarget) {
      const total = this.totalAmount()

      this.totalAmountTarget.textContent = formatNumber(total, {
        minimumFractionDigits: 1,
      })

      this.updatePrice()
    }
  }

  updatePrice() {
    if (this.hasWeightedPricePerMwhTarget) {
      const totalPrice = this.rowTargets.reduce((sum, row) => {
        const amount = parseNumber(
          row.querySelector(
            '[data-delivery-connection-table-target="amountInput"]',
          ).value,
        )
        const pricePerMwh = parseNumber(
          row.querySelector(
            '[data-delivery-connection-table-target="connectedPriceInput"]',
          ).value,
        )

        return sum + amount * pricePerMwh
      }, 0)

      const totalAmount = this.totalAmount()
      const pricePerMwh = totalAmount === 0 ? 0 : totalPrice / totalAmount
      const deliveryPriceInputTarget = document.getElementById(
        "delivery_price_per_mwh",
      )
      const deliveryConnectionTableCount = document.querySelectorAll(
        '[data-controller="delivery-connection-table"]',
      ).length

      this.weightedPricePerMwhTarget.textContent = pricePerMwh.toLocaleString(
        "de-CH",
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        },
      )

      if (deliveryConnectionTableCount > 1) {
        // We use a very similar method in delivery-connection-table_controller.js for a single table.
        if (
          !deliveryPriceInputTarget.value ||
          deliveryPriceInputTarget.dataset.manualAmount === "false"
        ) {
          deliveryPriceInputTarget.value = pricePerMwh.toFixed(2)
        }
      }
    }
  }

  totalAmount() {
    return this.amountInputTargets.reduce(
      (sum, input) => sum + parseNumber(input.value),
      0,
    )
  }

  updateDeliveryConnectionTable() {
    let anyVisible = false

    this.rowTargets.forEach((row) => {
      const purchaseTechnologies = row.dataset.technologies.split(",")
      const purchaseQualities = row.dataset.qualities.split(",")
      const purchaseCountries = row.dataset.countries.split(",")
      const purchaseQuarter = row.dataset.quarter

      let technologyMatches = true
      if (this.hasTechnologyInputTarget) {
        const technologyGroupId = this.technologyInputTarget.value
        const saleTechnologies = this.technologyGroupsValue[technologyGroupId]

        technologyMatches = purchaseTechnologies.every((technology) =>
          saleTechnologies.includes(technology),
        )
      }

      let countryMatches = true
      if (this.hasCountryInputTarget) {
        const countryGroupId = this.countryInputTarget.value
        const saleCountries = this.countryGroupsValue[countryGroupId]

        countryMatches = purchaseCountries.every((country) =>
          saleCountries.includes(country),
        )
      }

      let qualityMatches = true
      if (this.hasQualityInputTarget) {
        const qualityGroupId = this.qualityInputTarget.value
        const saleQualities = this.qualityGroupsValue[qualityGroupId]

        // downgrade option
        if (saleQualities.includes("naturemade")) {
          saleQualities.push("naturemade_star")
        }

        qualityMatches = purchaseQualities.every((quality) =>
          saleQualities.includes(quality),
        )
      }

      let quarterMatches = true
      if (this.hasQuarterInputTarget) {
        const saleQuarter = this.quarterInputTarget.value
        quarterMatches = purchaseQuarter == saleQuarter
      }

      const connectedAmount = parseNumber(
        row.querySelector(
          '[data-delivery-connection-table-target="amountInput"]',
        ).value,
      )

      if (
        connectedAmount > 0 ||
        (technologyMatches &&
          countryMatches &&
          qualityMatches &&
          quarterMatches)
      ) {
        anyVisible = true
        row.classList.remove("hidden")
      } else {
        row.classList.add("hidden")
      }
    })

    if (this.hasEmptyResultMessageTarget) {
      this.emptyResultMessageTarget.classList.toggle("hidden", anyVisible)
    }
  }
}
